/**
 * API Helper Utility
 * 
 * Centralized API request handling with authentication error management
 */

import { authStorage } from './authStorage';
import { Notify } from 'quasar';

/**
 * Handle authentication errors by clearing token and redirecting to login
 */
const handleAuthError = () => {
  
  // Clear authentication tokens
  authStorage.clearAll();
  localStorage.removeItem('auth_token'); // Clear old localStorage tokens as well
  
  // Show notification to user
  Notify.create({
    type: 'warning',
    message: 'Session expired. Please log in again.',
    timeout: 3000,
  });
  
  // Redirect to login with current path as redirect parameter after a short delay
  const currentPath = window.location.pathname + window.location.search;
  const redirectParam = currentPath && currentPath !== '/' ? '?redirect=' + encodeURIComponent(currentPath) : '';
  
  setTimeout(() => {
    window.location.href = '/login' + redirectParam;
  }, 500); // Small delay to allow notification to show
};

/**
 * Check response status and handle authentication errors
 * @param {Response} response - Fetch API response object
 * @returns {Response} The response if successful
 * @throws {Error} If response indicates an error
 */
export const checkAuthResponse = async (response) => {
  // Handle authentication errors
  if (response.status === 401) {
    handleAuthError();
    // Create a special error that components can recognize to prevent showing their own error messages
    const authError = new Error('Authentication required');
    authError.isAuthError = true;
    throw authError;
  }
  
  if (response.status === 403) {
    // For 403 errors, just show notification without redirect
    Notify.create({
      type: 'negative',
      message: 'Access denied. You do not have permission to perform this action.',
      timeout: 5000,
    });
    const forbiddenError = new Error('Access forbidden');
    forbiddenError.isForbiddenError = true;
    throw forbiddenError;
  }
  
  // Handle other non-ok responses
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    let errorData = null;
    try {
      errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response body isn't JSON, use status text
      errorMessage = response.statusText || errorMessage;
    }

    // Create error with status and data attached for special handling
    const error = new Error(errorMessage);
    error.status = response.status;
    error.data = errorData;
    throw error;
  }
  
  return response;
};

/**
 * Make an authenticated API request with proper error handling
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch API options
 * @returns {Promise<Response>} The fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {

  // Get the auth token
  const token = authStorage.getToken() || sessionStorage.getItem('token');

  // If no token available, redirect to login immediately
  if (!token) {
    handleAuthError();
    throw new Error('No authentication token available');
  }

  // Set up default headers
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  const fetchOptions = {
    ...options,
    headers,
  };


  try {
    const response = await fetch(url, fetchOptions);


    return checkAuthResponse(response);
  } catch (error) {
    // If it's a network error, don't redirect
    if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
      throw new Error('Network error - Please check your connection');
    }
    // Re-throw the error for the caller to handle
    throw error;
  }
};

/**
 * Get headers for authenticated requests
 * @returns {Object} Headers object with auth token
 */
export const getHeaders = () => {
  const token = authStorage.getToken() || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

export default {
  checkAuthResponse,
  authenticatedFetch,
  handleAuthError,
  getHeaders,
};